import React, { useEffect, useState } from 'react';
import { useVision, VisionTimeframe } from '../lib/stores/vision';
import { useLifeAreas } from '../lib/stores/lifeareas';
import Card from '../lib/design-system/components/Card';
import Button from '../lib/design-system/components/Button';

const TIMEFRAMES: Array<{ id: VisionTimeframe; label: string; description: string; emoji: string }> = [
  { id: '1-year', label: '1 Year', description: 'Short-term vision', emoji: '🎯' },
  { id: '3-year', label: '3 Years', description: 'Medium-term vision', emoji: '🚀' },
  { id: '5-year', label: '5 Years', description: 'Long-term vision', emoji: '🌟' },
  { id: '10-year', label: '10 Years', description: 'Decade vision', emoji: '🏔️' },
  { id: 'lifetime', label: 'Lifetime', description: 'Ultimate legacy', emoji: '🌅' },
];

export default function VisionPage() {
  const { visions, loading, error, initialize, saveVision, getVisionsByArea, initialized } = useVision();
  const { areas, initialize: initAreas, initialized: areasInitialized } = useLifeAreas();

  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<VisionTimeframe>('1-year');
  const [visionText, setVisionText] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!initialized) initialize();
    if (!areasInitialized) initAreas();
  }, [initialized, areasInitialized]);

  useEffect(() => {
    // Select first area by default
    if (areas.length > 0 && !selectedArea) {
      setSelectedArea(areas[0].id);
    }
  }, [areas, selectedArea]);

  useEffect(() => {
    // Load vision text when area or timeframe changes
    if (selectedArea) {
      const vision = visions.find(
        v => v.life_area_id === selectedArea && v.timeframe === selectedTimeframe
      );
      setVisionText(vision?.vision_text ?? '');
      setHasChanges(false);
    }
  }, [selectedArea, selectedTimeframe, visions]);

  const handleTextChange = (text: string) => {
    setVisionText(text);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (selectedArea && visionText.trim()) {
      await saveVision(selectedArea, selectedTimeframe, visionText.trim());
      setHasChanges(false);
    }
  };

  const getAreaName = (areaId: string) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.name : 'Unknown Area';
  };

  const getAreaIcon = (areaId: string) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.icon : '📌';
  };

  const getAreaColor = (areaId: string) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.color : '#6B7280';
  };

  const getVisionCount = (areaId: string) => {
    return getVisionsByArea(areaId).length;
  };

  if (loading && !initialized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading visions...</p>
      </div>
    );
  }

  const selectedAreaObj = areas.find(a => a.id === selectedArea);

  return (
    <div style={{ padding: '2rem', width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '2.5rem',
          fontWeight: 400,
          color: 'var(--color-neutral-900)',
          marginBottom: '0.5rem',
        }}>
          Vision Statements
        </h1>
        <p style={{ color: 'var(--color-neutral-600)', fontSize: '1.125rem' }}>
          Define your vision for each life area across multiple timeframes
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1.5rem',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 280px) 1fr', gap: 'var(--space-6)' }}>
        {/* Left Sidebar: Life Areas */}
        <div>
          <Card className="card-compact-desktop">
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              marginBottom: '1rem',
              color: 'var(--color-neutral-700)',
            }}>
              Select Life Area
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {areas.map(area => (
                <button
                  key={area.id}
                  onClick={() => setSelectedArea(area.id)}
                  style={{
                    padding: '0.75rem',
                    border: selectedArea === area.id ? `2px solid ${area.color}` : '1px solid var(--color-neutral-200)',
                    borderRadius: '8px',
                    background: selectedArea === area.id ? `${area.color}10` : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>{area.icon}</span>
                    <span style={{
                      fontWeight: selectedArea === area.id ? 600 : 400,
                      color: selectedArea === area.id ? area.color : 'var(--color-neutral-700)',
                      fontSize: '0.875rem',
                    }}>
                      {area.name}
                    </span>
                  </div>
                  {getVisionCount(area.id) > 0 && (
                    <span style={{
                      fontSize: '0.75rem',
                      color: 'var(--color-neutral-500)',
                      backgroundColor: 'var(--color-neutral-100)',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '12px',
                    }}>
                      {getVisionCount(area.id)}/5
                    </span>
                  )}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Right: Vision Editor */}
        <div>
          {selectedArea ? (
            <>
              {/* Area Header */}
              <div style={{
                marginBottom: '1.5rem',
                padding: '1.5rem',
                background: `linear-gradient(135deg, ${getAreaColor(selectedArea)}15 0%, ${getAreaColor(selectedArea)}05 100%)`,
                borderRadius: '12px',
                border: `2px solid ${getAreaColor(selectedArea)}30`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '2rem' }}>{getAreaIcon(selectedArea)}</span>
                  <h2 style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: '1.75rem',
                    fontWeight: 500,
                    color: 'var(--color-neutral-900)',
                    margin: 0,
                  }}>
                    {getAreaName(selectedArea)}
                  </h2>
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)', margin: 0 }}>
                  Completed {getVisionCount(selectedArea)} of 5 timeframes
                </p>
              </div>

              {/* Timeframe Selector */}
              <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {TIMEFRAMES.map(tf => {
                  const hasVision = visions.some(
                    v => v.life_area_id === selectedArea && v.timeframe === tf.id
                  );
                  return (
                    <button
                      key={tf.id}
                      onClick={() => setSelectedTimeframe(tf.id)}
                      style={{
                        padding: '0.75rem 1.25rem',
                        border: selectedTimeframe === tf.id ? `2px solid ${getAreaColor(selectedArea)}` : '1px solid var(--color-neutral-300)',
                        borderRadius: '8px',
                        background: selectedTimeframe === tf.id ? `${getAreaColor(selectedArea)}15` : 'white',
                        color: selectedTimeframe === tf.id ? getAreaColor(selectedArea) : 'var(--color-neutral-700)',
                        fontWeight: selectedTimeframe === tf.id ? 600 : 400,
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        position: 'relative',
                      }}
                    >
                      <span>{tf.emoji}</span>
                      <span>{tf.label}</span>
                      {hasVision && (
                        <span style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          width: '8px',
                          height: '8px',
                          backgroundColor: 'var(--color-success-500)',
                          borderRadius: '50%',
                          border: '2px solid white',
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Vision Editor */}
              <Card style={{ padding: '2rem' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>
                      {TIMEFRAMES.find(tf => tf.id === selectedTimeframe)?.emoji}
                    </span>
                    <h3 style={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: '1.5rem',
                      fontWeight: 500,
                      color: 'var(--color-neutral-900)',
                      margin: 0,
                    }}>
                      {TIMEFRAMES.find(tf => tf.id === selectedTimeframe)?.label} Vision
                    </h3>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
                    {TIMEFRAMES.find(tf => tf.id === selectedTimeframe)?.description}
                  </p>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    color: 'var(--color-neutral-700)',
                  }}>
                    Describe your vision for this area in {TIMEFRAMES.find(tf => tf.id === selectedTimeframe)?.label?.toLowerCase()}:
                  </label>
                  <textarea
                    value={visionText}
                    onChange={(e) => handleTextChange(e.target.value)}
                    placeholder={`Example: In ${TIMEFRAMES.find(tf => tf.id === selectedTimeframe)?.label?.toLowerCase()}, I see myself...`}
                    rows={8}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--color-neutral-300)',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      lineHeight: '1.6',
                    }}
                  />
                  <div style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: 'var(--color-neutral-500)',
                  }}>
                    {visionText.length} characters
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  disabled={!visionText.trim() || !hasChanges || loading}
                  style={{ width: '100%' }}
                >
                  {loading ? 'Saving...' : hasChanges ? 'Save Vision' : 'Saved ✓'}
                </Button>

                {!hasChanges && visionText && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    backgroundColor: 'var(--color-success-50)',
                    border: '1px solid var(--color-success-200)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color: 'var(--color-success-700)',
                    textAlign: 'center',
                  }}>
                    ✓ Vision saved successfully
                  </div>
                )}
              </Card>

              {/* Progress Overview */}
              <Card style={{ marginTop: '1.5rem', padding: '1.5rem' }}>
                <h4 style={{ marginBottom: '1rem', fontWeight: 600 }}>
                  {selectedAreaObj?.name} Vision Progress
                </h4>
                <div className="grid-dense">
                  {TIMEFRAMES.map(tf => {
                    const hasVision = visions.some(
                      v => v.life_area_id === selectedArea && v.timeframe === tf.id
                    );
                    return (
                      <div
                        key={tf.id}
                        style={{
                          padding: '0.75rem',
                          borderRadius: '6px',
                          border: `1px solid ${hasVision ? 'var(--color-success-300)' : 'var(--color-neutral-200)'}`,
                          backgroundColor: hasVision ? 'var(--color-success-50)' : 'var(--color-neutral-50)',
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ fontSize: '1.25rem', marginBottom: '0.25rem' }}>
                          {hasVision ? '✓' : tf.emoji}
                        </div>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: hasVision ? 'var(--color-success-700)' : 'var(--color-neutral-600)',
                        }}>
                          {tf.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </>
          ) : (
            <Card style={{ padding: '3rem', textAlign: 'center' }}>
              <p style={{ color: 'var(--color-neutral-500)' }}>
                Select a life area to begin creating vision statements
              </p>
            </Card>
          )}
        </div>
      </div>

      {/* Guidance */}
      <Card style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: 'var(--color-primary-50)' }}>
        <h3 style={{ marginBottom: '0.75rem', fontWeight: 600 }}>Vision Statement Tips</h3>
        <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8', color: 'var(--color-neutral-700)' }}>
          <li>Write in present tense as if you're already there ("I am..." not "I will...")</li>
          <li>Be specific and vivid - engage all your senses</li>
          <li>Focus on how you want to feel, not just what you want to achieve</li>
          <li>Start with shorter timeframes (1-year) to build momentum</li>
          <li>Longer timeframes can be more aspirational and values-driven</li>
        </ul>
      </Card>
    </div>
  );
}
